import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup
} from 'react-simple-maps';
import { statsData } from '../data/StatsData';
import { peopleData } from '../data/PeopleData';
import { wordData } from '../data/WordData';
import { locationData } from '../data/LocationData';
import SEO from '../components/SEO';

const PageContainer = styled.div`
  padding: 3rem;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.ink};
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;

  @media (max-width: 768px) {
    font-size: 2rem;
    letter-spacing: 1.5px;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
    letter-spacing: 1px;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #8A7E70;
  margin-bottom: 3rem;
  font-family: 'Courier New', monospace;
  font-style: italic;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    margin-bottom: 1.5rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const StatCard = styled.div`
  background: #FFFEF9;
  border: 1.5px solid #C4B5A0;
  padding: 2rem;
  box-shadow: 
    1px 1px 5px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  text-align: center;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem;
  }
`;

const StatValue = styled.div`
  font-size: 3rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.ink};
  font-family: 'Courier New', monospace;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #716859;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 0.85rem;
    letter-spacing: 0.5px;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const ChartSection = styled.div`
  background: #FFFEF9;
  border: 1.5px solid #C4B5A0;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 
    1px 1px 5px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  overflow-x: auto;

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.ink};
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-bottom: 2px solid #C4B5A0;
  padding-bottom: 0.75rem;

  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
    margin-bottom: 1rem;
    letter-spacing: 0.5px;
    padding-bottom: 0.5rem;
  }
`;

const MapContainer = styled.div`
  background: #FFFEF9;
  border: 1.5px solid #C4B5A0;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 
    1px 1px 5px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  overflow-x: auto;

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 0;
  }
`;

const MapLegend = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  color: #716859;

  @media (max-width: 480px) {
    font-size: 0.75rem;
    gap: 0.4rem;
  }
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.$color};
  border: 1px solid #C4B5A0;

  @media (max-width: 480px) {
    width: 10px;
    height: 10px;
  }
`;

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Comprehensive location coordinates for mapping
const locationCoordinates = {
  // North America - US Cities & States
  'New York': [-74.006, 40.7128],
  'New York City': [-74.006, 40.7128],
  'NY': [-74.006, 40.7128],
  'NYC': [-74.006, 40.7128],
  'Manhattan': [-73.9712, 40.7831],
  'Brooklyn': [-73.9442, 40.6782],
  'Palm Beach': [-80.0533, 26.7056],
  'West Palm Beach': [-80.0533, 26.7153],
  'Palm Beach County': [-80.0533, 26.7056],
  'Miami': [-80.1918, 25.7617],
  'Florida': [-81.5158, 27.6648],
  'FL': [-81.5158, 27.6648],
  'Los Angeles': [-118.2437, 34.0522],
  'San Francisco': [-122.4194, 37.7749],
  'Chicago': [-87.6298, 41.8781],
  'Boston': [-71.0589, 42.3601],
  'Washington': [-77.0369, 38.9072],
  'DC': [-77.0369, 38.9072],
  'White House': [-77.0365, 38.8977],
  'California': [-119.4179, 36.7783],
  'CA': [-119.4179, 36.7783],
  'Texas': [-99.9018, 31.9686],
  'New Mexico': [-106.2371, 34.5199],
  'Arizona': [-111.0937, 34.0489],
  'Utah': [-111.0937, 39.3210],
  'Hawaii': [-155.5828, 19.8968],
  'New Jersey': [-74.4057, 40.0583],
  'Massachusetts': [-71.3824, 42.4072],
  'MA': [-71.3824, 42.4072],
  
  // US Territories & Caribbean
  'Virgin Islands': [-64.8963, 18.3358],
  'St. Thomas': [-64.9307, 18.3381],
  'Little St. James': [-64.8235, 18.3001],
  'Mar-a-Lago': [-80.0369, 26.6774],
  
  // Canada
  'Canada': [-106.3468, 56.1304],
  
  // Mexico & Central America
  'Mexico': [-102.5528, 23.6345],
  
  // South America
  'Brazil': [-51.9253, -14.2350],
  
  // Europe - Western
  'United Kingdom': [-3.4360, 55.3781],
  'UK': [-0.1276, 51.5074],
  'England': [-1.1743, 52.3555],
  'London': [-0.1276, 51.5074],
  'Britain': [-3.4360, 55.3781],
  'France': [2.2137, 46.2276],
  'Paris': [2.3522, 48.8566],
  'Germany': [10.4515, 51.1657],
  'Berlin': [13.4050, 52.5200],
  'Spain': [-3.7492, 40.4637],
  'Italy': [12.5674, 41.8719],
  'Switzerland': [8.2275, 46.8182],
  'Greece': [21.8243, 39.0742],
  
  // Europe - Eastern
  'Russia': [105.3188, 61.5240],
  'Moscow': [37.6173, 55.7558],
  'Ukraine': [31.1656, 48.3794],
  
  // Middle East
  'Israel': [34.8516, 31.0461],
  'Jerusalem': [35.2137, 31.7683],
  'Tel Aviv': [34.7818, 32.0853],
  'West Bank': [35.2332, 31.9522],
  'Gaza': [34.4668, 31.3547],
  'Palestine': [35.2332, 31.9522],
  'Saudi Arabia': [45.0792, 23.8859],
  'Dubai': [55.2708, 25.2048],
  'Iran': [53.6880, 32.4279],
  'Iraq': [43.6793, 33.2232],
  'Syria': [38.9968, 34.8021],
  'Lebanon': [35.8623, 33.8547],
  'Jordan': [36.2384, 30.5852],
  'Qatar': [51.1839, 25.3548],
  'Kuwait': [47.4818, 29.3117],
  'Turkey': [35.2433, 38.9637],
  'Egypt': [30.8025, 26.8206],
  'Cairo': [31.2357, 30.0444],
  'Libya': [17.2283, 26.3351],
  
  // Asia - East
  'China': [104.1954, 35.8617],
  'Beijing': [116.4074, 39.9042],
  'Hong Kong': [114.1694, 22.3193],
  'Japan': [138.2529, 36.2048],
  'Taiwan': [120.9605, 23.6978],
  'South Korea': [127.7669, 35.9078],
  'North Korea': [127.5101, 40.3399],
  
  // Asia - South & Southeast
  'India': [78.9629, 20.5937],
  'Pakistan': [69.3451, 30.3753],
  'Afghanistan': [67.7100, 33.9391],
  'Singapore': [103.8198, 1.3521],
  'Indonesia': [113.9213, -0.7893],
  
  // Middle East Regional
  'Middle East': [47.5769, 29.3117],
  'Sinai': [33.9751, 29.5000],
  
  // Africa
  'South Africa': [22.9375, -30.5595],
  
  // Continents & Regions
  'United States': [-95.7129, 37.0902],
  'USA': [-95.7129, 37.0902],
  'US': [-95.7129, 37.0902],
  'U.S.': [-95.7129, 37.0902],
  'America': [-95.7129, 37.0902],
  'Europe': [15.2551, 54.5260],
  'Asia': [100.6197, 34.0479],
  'Africa': [8.7832, 6.5244],
  'Australia': [133.7751, -25.2744],
  
  // Universities & Institutions
  'Harvard': [-71.1167, 42.3770],
  'Harvard University': [-71.1167, 42.3770],
  'Cambridge': [-71.1097, 42.3736],
  'MIT': [-71.0942, 42.3601],
  'Arizona State University': [-111.9391, 33.4255],
};

function Stats() {
  const navigate = useNavigate();

  const handleSearchClick = (query) => {
    // Navigate to search page with the clicked item as query
    navigate(`/?q=${encodeURIComponent(query)}`);
  };

  // Determine if mobile for chart adjustments
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prepare data for charts (limit to top 15 for readability, less on mobile)
  const topPeopleChart = peopleData.slice(0, isMobile ? 10 : 15).map(p => ({
    name: p.name.length > 25 ? p.name.substring(0, 25) + '...' : p.name,
    fullName: p.name,
    mentions: p.count
  }));

  const topWordsChart = wordData.slice(0, isMobile ? 15 : 20);

  // Prepare location markers for map (only locations with known coordinates)
  const locationMarkers = locationData
    .filter(loc => locationCoordinates[loc.location])
    .map(loc => ({
      name: loc.location,
      coordinates: locationCoordinates[loc.location],
      mentions: loc.count,
      type: 'location'
    }));

  const getMarkerColor = (mentions) => {
    // Color based on frequency
    if (mentions > 1000) return '#D4847C'; // Red - Very high
    if (mentions > 500) return '#E8A598';  // Light red
    if (mentions > 300) return '#A89B88';  // Brown
    if (mentions > 100) return '#C4B5A0';  // Tan
    return '#8A7E70'; // Gray - Lower frequency
  };

  const getMarkerSize = (mentions) => {
    // Size based on frequency
    if (mentions > 1000) return 14;
    if (mentions > 500) return 11;
    if (mentions > 300) return 9;
    if (mentions > 100) return 7;
    if (mentions > 50) return 5;
    return 4;
  };

  return (
    <>
      <SEO 
        title="Statistics" 
        description="Analysis of the Epstein Archive: Total documents, most mentioned people, locations map, and keyword frequency."
      />
      <PageContainer>
        <Title>Archive Statistics</Title>
        <Subtitle>Comprehensive analysis of document contents</Subtitle>

        {/* Summary Stats */}
        <StatsGrid>
          <StatCard>
            <StatValue>{statsData.totalDocuments.toLocaleString()}</StatValue>
            <StatLabel>Total Documents</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{statsData.uniquePeople.toLocaleString()}</StatValue>
            <StatLabel>Unique People</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{statsData.uniqueLocations.toLocaleString()}</StatValue>
            <StatLabel>Unique Location Mentions</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{statsData.uniqueDates.toLocaleString()}</StatValue>
            <StatLabel>Unique Date References</StatLabel>
          </StatCard>
        </StatsGrid>

        {/* Most Mentioned People */}
        <ChartSection>
          <SectionTitle>Most Mentioned People (Click to Search)</SectionTitle>
          <ResponsiveContainer width="100%" height={isMobile ? 400 : 500} minWidth={isMobile ? 320 : undefined}>
            <BarChart
              data={topPeopleChart}
              layout="vertical"
              margin={isMobile 
                ? { top: 5, right: 10, left: 100, bottom: 5 } 
                : { top: 5, right: 30, left: 150, bottom: 5 }
              }
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#D4CFC4" />
              <XAxis type="number" stroke="#716859" style={{ fontFamily: 'Courier New' }} />
              <YAxis 
                type="category" 
                dataKey="name" 
                stroke="#716859" 
                style={{ fontFamily: 'Courier New', fontSize: isMobile ? '0.7rem' : '0.85rem' }}
                width={isMobile ? 90 : 140}
              />
              <Tooltip 
                contentStyle={{ 
                  background: '#FFFEF9', 
                  border: '1.5px solid #C4B5A0',
                  fontFamily: 'Courier New',
                  borderRadius: '2px'
                }}
                labelFormatter={(label, payload) => payload[0]?.payload?.fullName || label}
                formatter={(value) => `${value.toLocaleString()} mentions`}
              />
              <Bar 
                dataKey="mentions" 
                fill="#D4847C"
                onClick={(data, index, e) => {
                  if (data && data.fullName) {
                    handleSearchClick(data.fullName);
                  }
                }}
                style={{ cursor: 'pointer' }}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartSection>

        {/* Most Mentioned Words */}
        <ChartSection>
          <SectionTitle>Most Frequent Keywords (Click to Search)</SectionTitle>
          <ResponsiveContainer width="100%" height={isMobile ? 350 : 400} minWidth={isMobile ? 320 : undefined}>
            <BarChart
              data={topWordsChart}
              margin={isMobile 
                ? { top: 5, right: 10, left: 10, bottom: 70 } 
                : { top: 5, right: 30, left: 20, bottom: 60 }
              }
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#D4CFC4" />
              <XAxis 
                dataKey="word" 
                stroke="#716859" 
                style={{ fontFamily: 'Courier New', fontSize: isMobile ? '0.7rem' : '0.8rem' }}
                angle={-45}
                textAnchor="end"
                height={isMobile ? 90 : 80}
              />
              <YAxis stroke="#716859" style={{ fontFamily: 'Courier New' }} />
              <Tooltip 
                contentStyle={{ 
                  background: '#FFFEF9', 
                  border: '1.5px solid #C4B5A0',
                  fontFamily: 'Courier New',
                  borderRadius: '2px'
                }}
                labelFormatter={(label, payload) => payload[0]?.payload?.word || label}
                formatter={(value) => `${value.toLocaleString()} occurrences`}
              />
              <Bar 
                dataKey="count" 
                fill="#A89B88"
                onClick={(data, index, e) => {
                  if (data && data.word) {
                    handleSearchClick(data.word);
                  }
                }}
                style={{ cursor: 'pointer' }}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartSection>

        {/* Top Locations */}
        <ChartSection>
          <SectionTitle>Most Mentioned Locations (Click to Search)</SectionTitle>
          <ResponsiveContainer width="100%" height={isMobile ? 350 : 400} minWidth={isMobile ? 320 : undefined}>
            <BarChart
              data={locationData.slice(0, isMobile ? 10 : 15)}
              layout="vertical"
              margin={isMobile 
                ? { top: 5, right: 10, left: 80, bottom: 5 } 
                : { top: 5, right: 30, left: 120, bottom: 5 }
              }
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#D4CFC4" />
              <XAxis type="number" stroke="#716859" style={{ fontFamily: 'Courier New' }} />
              <YAxis 
                type="category" 
                dataKey="location" 
                stroke="#716859" 
                style={{ fontFamily: 'Courier New', fontSize: isMobile ? '0.7rem' : '0.85rem' }}
                width={isMobile ? 70 : 110}
              />
              <Tooltip 
                contentStyle={{ 
                  background: '#FFFEF9', 
                  border: '1.5px solid #C4B5A0',
                  fontFamily: 'Courier New',
                  borderRadius: '2px'
                }}
                labelFormatter={(label, payload) => payload[0]?.payload?.location || label}
                formatter={(value) => `${value.toLocaleString()} mentions`}
              />
              <Bar 
                dataKey="count" 
                fill="#716859"
                onClick={(data, index, e) => {
                  if (data && data.location) {
                    handleSearchClick(data.location);
                  }
                }}
                style={{ cursor: 'pointer' }}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartSection>

        {/* Interactive World Map */}
        <MapContainer>
          <SectionTitle>Geographic Mentions</SectionTitle>
          <div style={{ 
            background: '#F5F5F0', 
            padding: isMobile ? '0.25rem' : '0.5rem', 
            border: '1px solid #D4CFC4', 
            minHeight: isMobile ? '300px' : '500px',
            overflowX: 'auto'
          }}>
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{
                scale: isMobile ? 100 : 140,
              }}
              width={isMobile ? 600 : 800}
              height={isMobile ? 350 : 500}
              style={{ width: '100%', height: 'auto', minWidth: isMobile ? '320px' : undefined }}
            >
              <ZoomableGroup center={[-20, 30]} zoom={isMobile ? 0.8 : 1}>
                <Geographies geography={geoUrl}>
                  {({ geographies }) =>
                    geographies.map((geo) => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill="#F0E8D8"
                        stroke="#C4B5A0"
                        strokeWidth={0.5}
                        style={{
                          default: { outline: 'none' },
                          hover: { fill: '#E8DCC8', outline: 'none' },
                          pressed: { outline: 'none' },
                        }}
                      />
                    ))
                  }
                </Geographies>
                {locationMarkers.map(({ name, coordinates, mentions }) => (
                  <Marker 
                    key={name} 
                    coordinates={coordinates}
                    onClick={() => handleSearchClick(name)}
                  >
                    <circle
                      r={getMarkerSize(mentions)}
                      fill={getMarkerColor(mentions)}
                      stroke="#FFFEF9"
                      strokeWidth={2}
                      style={{ cursor: 'pointer' }}
                    />
                    <title>{`${name}: ${mentions.toLocaleString()} mentions (click to search)`}</title>
                  </Marker>
                ))}
              </ZoomableGroup>
            </ComposableMap>
          </div>
          <MapLegend>
            <LegendItem>
              <LegendDot $color="#D4847C" />
              1,000+ mentions
            </LegendItem>
            <LegendItem>
              <LegendDot $color="#E8A598" />
              500-1,000
            </LegendItem>
            <LegendItem>
              <LegendDot $color="#A89B88" />
              300-500
            </LegendItem>
            <LegendItem>
              <LegendDot $color="#C4B5A0" />
              100-300
            </LegendItem>
            <LegendItem>
              <LegendDot $color="#8A7E70" />
              &lt;100
            </LegendItem>
            <LegendItem style={{ marginLeft: '1rem', fontStyle: 'italic' }}>
              Click markers to search • Larger = more mentions
            </LegendItem>
          </MapLegend>
        </MapContainer>
      </PageContainer>
    </>
  );
}

export default Stats;

